import type { ExecuteVariant, PlayerAttackContactKind } from './types'
import { currentDodgeVariant, currentWalkVariant } from './player_locomotion_trial'
import { lunaWalkAnimNames, lunaWalkNewArt } from './player_walk_new'
import { counterAnimationName } from './dodge_counter_motion'

// player.ts の PlayerState のうち、描画で参照しうる値。
// Spine 化済: idle / attacking / parrying / stepping / stunned (render.ts のゲートで接続)。
// executing は variant で execute / execute_finisher を出し分ける (アニメ未実装なら render が旧経路へ)。
// stunned は体幹崩れの非ループ崩れ姿勢 (完走後は最終フレーム保持で stunMs 中ずっと崩れたまま)。
// ※ exhausted (息切れ) は 2026-07-27 に廃止。スタミナの概念ごと畳んだため、この state はもう来ない。
//    Spine 側の exhausted アニメは skeleton に残してあるが、解決先が無いので再生されない。
// ※ hurt (被弾フリンチ) はここに足さない。 PlayerState ではなく render.ts の visual override で出す
//    (Player.hurtVisualUntil。 afterglow と同じ overrideAnimationName 機構)。 状態機械を汚さないため。
// ※ walking は PlayerState ではなく描画専用の派生 state。 player.ts では移動は state==='idle' のまま
//    起きる (x を動かすだけ) ので、 render.ts が「idle かつ移動中」を検出して walking へ昇格させる。
//    walk はループするため override(loop固定false)機構では出せず、 ここで正式解決する必要がある。
// ※ walking_back = 後退 (facing はボス固定なので「向きと逆へ移動」= ボスから離れる)。 前進 walk の
//    逆再生は邪道 (ムーンウォーク・runtime で凍る) なので専用の後退アニメ walk_back を当てる。
//    LUNAIRE は「役を演じる舞台」= 後ろシャッセ (品のあるバックスキップ) で慎重に間合いを譲る。
export type PlayerVisualState =
  | 'idle' | 'walking' | 'walking_back' | 'attacking' | 'counter' | 'parrying' | 'stepping'
  | 'guarding' | 'executing' | 'stunned' | 'defeat' | 'grabbed'

/**
 * parry が「受けた姿で静止している」時刻 (秒)。 guard アニメはこの姿から作られる
 * (scripts/author_guard_hold_from_parry.mjs)。
 *   parry は 0.033〜0.200s が受け姿での静止区間 (骨の値が一定) で、0.200s から素立ちへほどき始める。
 *   ゲーム側の弾き判定終了 = 200ms と一致する。
 *   ⚠️ parry のキー時刻を変えたらここも見直す (tests/guard_from_parry_hold.test.ts が固定)。
 */
export const PARRY_HOLD_SEC = 0.18
/** parry の「ほどき」が始まる時刻。 guard_out はここ以降を写している。 */
export const PARRY_UNWIND_SEC = 0.2
/** ほどきの尺。 render.ts が guard_out を出しておく時間。 */
export const PARRY_TAIL_SEC = 0.5666666 - PARRY_UNWIND_SEC
/** 致命を弾いて押された後の、重い受けの姿からのほどき (parry_heavy_out) の尺。
 *  scripts/author_parry_heavy_from_parry.mjs が parry の 0.17 秒以降を写して作る (DEV ?execPush=new・src/exec_parry.ts)。 */
export const PARRY_HEAVY_OUT_SEC = 0.4
/** 名指しの姿 (override) を流し切った後に自動で繋ぐアニメ。 ガードで受けた姿 (guard_hit・src/ukemi.ts) → 受け構えのループ。 */
export const OVERRIDE_THEN: Readonly<Record<string, { name: string, loop: boolean }>> = {
  guard_hit: { name: 'guard', loop: true },
}

export interface SpineAnimResolution {
  /** Spine の Animation 名 (メイントラックで再生) */
  name: string
  /** loop 再生するか */
  loop: boolean
  /**
   * 完走後に自動で繋ぐアニメ (Spine のトラックへ予約する)。
   *   「短い繋ぎ → 本体のループ」を1つの state で出すために使う (受け構えの入り → 保持)。
   */
  then?: { name: string, loop: boolean }
}

/**
 * 連撃の段ごとのアニメ候補 (先頭から順に、skeleton に実在する最初のものを使う)。
 *   連撃を切っている間 (配布版の既定・comboOn=false) は 1本の攻撃 = attack (+ 接触別 attack_guarded/shallow/deep)。
 *   連撃を試す間 (comboOn=true) は 1段目 = attack_1、2段目 = attack_2、3段目 = attack_3 (時間割 B に合わせて作った骨のアニメ)。
 *   接触別は attack_2_shallow のように段名 + 接触。段のアニメが無ければ旧の attack 系へ落ちる。
 *   ⚠️ 旧 attack は連撃を切っている間と見切りカウンターの経路が旧の時間割で使うので、連撃用に作り替えない。
 */
export function playerAttackAnimationCandidates(
  comboStep: number,
  kind: PlayerAttackContactKind | null,
  comboOn = false,
): string[] {
  const contact = kind === 'guarded' || kind === 'shallow' || kind === 'deep' ? kind : null
  const out: string[] = []
  if (comboOn || comboStep > 0) {
    const base = `attack_${comboStep + 1}`
    if (contact) out.push(`${base}_${contact}`)
    out.push(base)
  }
  if (contact) out.push(`attack_${contact}`)
  out.push('attack')
  return out
}

/**
 * 連撃をやめた時の戻り (構え直し → 素立ち) の尺 (秒)。 描画専用の attack_settle を流しておく時間。
 *   2026-10-03「1発ごとに素立ちへ戻るので単発を3回に見える」の直し: 1・2段目は構え直しの姿で終わり、
 *   次を押さなかった時だけ render.ts がこれを idle の前に流す (ゲーム側は従来どおり即 idle = 操作は止めない)。
 *   ⚠️ scripts/author_player_attack_combo.mjs の SETTLE_SEC と同じ (tests/player_attack_combo.test.ts が照合)。
 */
export const ATTACK_SETTLE_SEC = 0.28

/** 連撃の段 (0 始まり) が終わって idle へ戻った時、構え直しからの戻り (attack_settle) を流すか。
 *  1・2段目 (構え直しで終わる) だけ。 3段目は素立ちで終わる・見切りカウンターは別のアニメ。 */
export function needsAttackSettle(comboStep: number, isCounter: boolean, comboOn: boolean): boolean {
  return comboOn && !isCounter && comboStep < 2
}

/** 通常突きの前半を共有し、接触後の戻りだけを差し替える描画専用アニメ名。 */
export function resolvePlayerAttackContactAnimation(
  visualState: PlayerVisualState,
  kind: PlayerAttackContactKind | null,
): string | undefined {
  // deathBeat / finisher 等が attacking より上位の見た目へ切り替えた後は、
  // Player.state に接触結果が残っていても攻撃リカバリで上書きしない。
  if (visualState !== 'attacking') return undefined
  switch (kind) {
    case 'guarded': return 'attack_guarded'
    case 'shallow': return 'attack_shallow'
    case 'deep': return 'attack_deep'
    // counter は既存の専用アニメが全身の沈み込みまで担当する。
    case 'counter':
    case null:
    default: return undefined
  }
}

/** アニメ: idle / attack / parry / dodge / execute / execute_finisher / stunned。
 *  parrying→parry, stepping→dodge, executing→execute(_finisher), stunned→stunned。
 *  未対応 state は idle に倒す。
 *  @param executeVariant executing 時のみ参照。 既定は通常致命 (execute)。 */
export function resolveSpineAnimation(
  state: PlayerVisualState,
  executeVariant: ExecuteVariant = 'normal',
): SpineAnimResolution {
  switch (state) {
    case 'walking':
      // 移動中 (戦闘外・中立)。 ループ。 walk アニメ未 author (空) なら drawSpinePlayer 側の
      // fallback で idle へ倒れる (空 walk = setup ポーズを出さない保険)。
      // DEV ?lunaWalk=new|newslow|v2|v3|v4run|v4walk|v5|v6a|v6b|v6c|v7a|v7b (2026-10-12・src/player_walk_new.ts) は作り直した歩き (walk_n・v2 は walk_v2・v3 は小走り walk_v3)
      if (lunaWalkNewArt()) return { name: lunaWalkAnimNames().forward, loop: true }
      return { name: 'walk' + currentWalkVariant().suffix, loop: true }
    case 'walking_back':
      // 後退 (ボスから離れる)。 専用バックシャッセを通常再生 (逆再生でない)。 未 author なら idle 退行。
      if (lunaWalkNewArt()) return { name: lunaWalkAnimNames().back, loop: true }
      return { name: 'walk_back' + currentWalkVariant().suffix, loop: true }
    case 'attacking':
      return { name: 'attack', loop: false }
    case 'counter':
      // 見切りカウンターの突き (沈み込む深い刺突)。 非ループ。 attacking のうち attackIsCounter のみ
      //   render.ts のゲートで counter へ振られる (通常攻撃は attack のまま)。
      //   既定は回避の止まり姿から始まる counter_from_dodge (10-09 採用・src/dodge_counter_motion.ts)。旧の counter は DEV ?counterFromDodge=0。
      return { name: counterAnimationName(), loop: false }
    case 'parrying':
      return { name: 'parry', loop: false }
    case 'guarding':
      // 受け構え = **パリィの受け姿から作った専用3本** (2026-07-20 実機FBで再設計)。
      //   guard_in (跳ね上がった髪が落ち着く繋ぎ) → guard (落ち着いた受け姿 + 呼吸のループ)。
      //   離した時は render.ts が guard_out (ほどき) を出す。
      //   3本とも F0 が直前の姿と寸分違わないので、繋ぎは全て mix 0 = 混色が起きない
      //   (spine_player の setMix。 混色させると出て行く parry が勝手にほどけて「カクッ」と見える)。
      //   出所: Lies of P / SEKIRO とも「弾きと受けは同じ動作の入りと持続」(2026-07-20 調査)。
      //   生成: scripts/author_guard_hold_from_parry.mjs (⚠️ 再 export 後は再実行)。
      return { name: 'guard_in', loop: false, then: { name: 'guard', loop: true } }
    case 'stepping':
      // 長さ違いの版 (DEV ?dodgeLen=b|c・src/player_locomotion_trial.ts) は dodge_b / dodge_c
      return { name: currentDodgeVariant().anim, loop: false }
    case 'executing':
      return { name: executeVariant === 'finisher' ? 'execute_finisher' : 'execute', loop: false }
    case 'stunned':
      // 体幹崩れ: 非ループ。 完走後は最終フレームを保持し、 stunMs (1800ms) 中 idle へ戻さない。
      return { name: 'stunned', loop: false }
    case 'grabbed':
      // 掴み拘束 (処刑人 D8/D9): 専用の掴みアニメは未 author (絵発注はステージ2)。
      //   当面 stunned の崩れ姿を代用 (switch 網羅を満たす程度 = 新規演出は作らない)。
      return { name: 'stunned', loop: false }
    case 'defeat':
      // 死亡 (崩れ込み): 非ループ。 死の一拍中に再生し、 完走後は最終フレーム (伏した姿) を保持。
      return { name: 'defeat', loop: false }
    case 'idle':
    default:
      return { name: 'idle', loop: true }
  }
}
